import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { FilterSortSelections, GatheringStore } from "src/contexts/GatheringContext/gatheringStore";
import EventTopic from "src/helpers/EventTopic";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export interface GatheringFilterSortStore {
	gatheringId: string;
	closeSelf: () => void;
	apply: GatheringStore["setFiltersAndSort"];
	reset: () => void;

	currentScreen: "Trunk" | "Leaf";

	getAllFilterSortSelections: () => FilterSortSelections;
	sortMode: FilterSortSelections["sortMode"];
	filterPhotoVariant: FilterSortSelections["filterPhotoVariant"];
	filterUserIds: FilterSortSelections["filterUserIds"];
	filterUploaderIds: FilterSortSelections["filterUploaderIds"];
	filterLabels: FilterSortSelections["filterLabels"];
	filterRestrictedLabels: FilterSortSelections["filterRestrictedLabels"];
	numColumnsTarget: FilterSortSelections["numColumnsTarget"];

	selectSort: (option: FilterSortSelections["sortMode"]) => void;
	toggleFilterPhotoVariant: (variant: PhotoVariant) => void;
	toggleFilterUserId: (userId: string) => void;
	toggleFilterUploaderId: (uploaderId: string) => void;
	toggleFilterLabel: (label: string) => void;
	toggleFilterRestrictedLabel: (label: string) => void;
	setNumColumnsTarget: (numColumnsTarget: number) => void;

	refreshTopic: EventTopic<{
		refresh: {
			props: {};
		};
	}>;
}

export type GatheringFilterSortState = Pick<
	GatheringFilterSortStore,
	| "sortMode"
	| "currentScreen"
	| "filterPhotoVariant"
	| "filterUserIds"
	| "filterUploaderIds"
	| "filterLabels"
	| "filterRestrictedLabels"
	| "numColumnsTarget"
	| "refreshTopic"
>;

const defaultState: GatheringFilterSortState = {
	sortMode: "UploadedTime",
	currentScreen: "Trunk",
	filterPhotoVariant: [],
	filterUserIds: [],
	filterUploaderIds: [],
	filterLabels: [],
	filterRestrictedLabels: [],
	numColumnsTarget: undefined,
	refreshTopic: new EventTopic(),
};

const getToggledArrayValue = <T>(newValue: T, filterArr: T[]) => {
	if (filterArr.includes(newValue)) {
		const newFilterArr = filterArr.filter((value) => value !== newValue);
		return newFilterArr;
	}
	return [...filterArr, newValue];
};

export const createGatheringFilterSortStore = (
	initialState: Partial<GatheringFilterSortState> &
		Pick<GatheringFilterSortStore, "closeSelf" | "gatheringId" | "apply">,
) => {
	return createStore<GatheringFilterSortStore>()(
		subscribeWithSelector((set, get) => ({
			...defaultState,
			...initialState,
			reset: () => {
				set(() => defaultState);
			},
			getAllFilterSortSelections: () => {
				const {
					sortMode,
					filterPhotoVariant,
					filterUploaderIds,
					filterUserIds,
					filterLabels,
					filterRestrictedLabels,
					numColumnsTarget,
				} = get();
				return {
					sortMode,
					filterPhotoVariant,
					filterUploaderIds,
					filterUserIds,
					filterLabels,
					filterRestrictedLabels,
					numColumnsTarget,
				};
			},
			selectSort: (option) => {
				if (get().sortMode === option) return;
				set(() => {
					return { sortMode: option };
				});
			},
			toggleFilterPhotoVariant: (variant) => {
				set((state) => {
					return {
						filterPhotoVariant: getToggledArrayValue(variant, state.filterPhotoVariant),
					};
				});
			},
			toggleFilterUserId: (userId) => {
				set((state) => {
					return {
						filterUserIds: getToggledArrayValue(userId, state.filterUserIds),
					};
				});
			},
			toggleFilterUploaderId: (uploaderId) => {
				set((state) => {
					return {
						filterUploaderIds: getToggledArrayValue(
							uploaderId,
							state.filterUploaderIds,
						),
					};
				});
			},
			toggleFilterLabel: (label) => {
				set((state) => {
					return {
						filterLabels: getToggledArrayValue(label, state.filterLabels),
					};
				});
			},
			toggleFilterRestrictedLabel: (label) => {
				set((state) => {
					return {
						filterRestrictedLabels: getToggledArrayValue(
							label,
							state.filterRestrictedLabels,
						),
					};
				});
			},
			setNumColumnsTarget: (numColumnsTarget) => {
				set(() => {
					return { numColumnsTarget };
				});
			},
		})),
	);
};
